import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { IoMdCall } from "react-icons/io";
import {
  FaChevronCircleLeft,
} from "react-icons/fa";
import { FaCircleChevronRight } from "react-icons/fa6";
import { IoSearch } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import {
  useToast,
} from '@chakra-ui/react';
import { FiUser } from "react-icons/fi";
import UserDetailsModal from "../components/UserDetailsModal";
const PAGE_SIZE = 25;

export default function CallTransactions() {
  const navigate = useNavigate();
  const toast = useToast();

  // ================= CALL TRANSACTIONS STATE =================

  const [activeView, setActiveView] = useState("history"); // "history" | "user-history"
  const [selectedTx, setSelectedTx] = useState(null);

  const adminUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("adminUser") || "{}");
    } catch {
      return {};
    }
  }, []);

  const canExport = adminUser?.email === "admin@arrowcoders.com";
  const [viewUser, setViewUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userLimit, setUserLimit] = useState(PAGE_SIZE);
  const [totalTransactions, setTotalTransactions] = useState(0);



  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [callTypeFilter, setCallTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const historyReqRef = useRef(null);
  const recentReqRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userSearchTimeout, setUserSearchTimeout] = useState(null);
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  // const [userLastCalls, setUserLastCalls] = useState({}); // { userId: { personName, duration, callType, date } }
  const [userCallStatusFilter, setUserCallStatusFilter] = useState("all");
const[overview,setOverview]=useState('');
  // ================= CALL TRANSACTIONS FETCH =================
  const toServerCallType = (v) => {
    if (!v || v === "all") return undefined;
    return String(v).toUpperCase();
  };
  const toServerStatus = (v) => {
    if (!v || v === "all") return undefined;
    return String(v).toUpperCase();
  };

  useEffect(() => {
    if (activeView !== "history") return;
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);
    return () => clearTimeout(t);
  }, [activeView, search]);

  const fetchCallTransactions = useCallback(
    async (page = 1) => {

      setError(null);
      try {
        if (historyReqRef.current) historyReqRef.current.abort();
        const controller = new AbortController();
        historyReqRef.current = controller;

        const params = { page, limit: PAGE_SIZE};
        if (debouncedSearch) params.search = debouncedSearch;

         if (statusFilter && statusFilter !== "all") {
        params.status = statusFilter.toLowerCase();
      }

        const ct = toServerCallType(callTypeFilter);
        const st = toServerStatus(statusFilter);
        if (ct) params.callType = ct;
        if (st) params.status = st;

        const res = await axios.get(
          "/api/v1/app/call-transactions/history",
          { params, signal: controller.signal }
        );



        if (res?.data?.status === "OK" && res.data.data) {
          const data = res.data.data;
          setTransactions(data.transactions || []);
          setCurrentPage(data.currentPage || page);
          setTotalPages(data.totalPages || 1);
          setTotalTransactions(data.totalTransactions ?? 0);
        } else {
          if (Array.isArray(res?.data)) {
            setTransactions(res.data);
            setCurrentPage(1);
            setTotalPages(1);
            setTotalTransactions(res.data.length);
          } else {
            setTransactions([]);
            setCurrentPage(1);
            setTotalPages(1);
            setTotalTransactions(0);
          }
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError") return;
        console.error("Error fetching call transactions:", err);
        setError("Failed to load transactions");
      } finally {

      }
    },
    [debouncedSearch, callTypeFilter, statusFilter]
  );

  const fetchRecentCalls = useCallback(async () => {
    try {
      if (recentReqRef.current) recentReqRef.current.abort();
      const controller = new AbortController();
      recentReqRef.current = controller;

      const params = { page: 1, limit: 5 };
      if (debouncedSearch) params.search = debouncedSearch;
      const ct = toServerCallType(callTypeFilter);
      const st = toServerStatus(statusFilter);
      if (ct) params.callType = ct;
      if (st) params.status = st;
      const res = await axios.get("/api/v1/app/call-transactions/history", { params, signal: controller.signal });
      if (res?.data?.status === "OK" && res.data.data) {
        setRecentCalls(res.data.data.transactions || []);
      } else if (Array.isArray(res?.data)) {
        setRecentCalls(res.data.slice(0, 5));
      } else {
        setRecentCalls([]);
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError") return;
      setRecentCalls([]);
    }
  }, [debouncedSearch, callTypeFilter, statusFilter]);

  const exportToExcel = async () => {
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      const ct = toServerCallType(callTypeFilter);
      const st = toServerStatus(statusFilter);
      if (ct) params.callType = ct;
      if (st) params.status = st;

      const res = await axios.get(
        "/api/v1/app/call-transactions/export-xls",
        {
          params,
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "call-history.xlsx");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("❌ XLS export failed", err);
    }
  };
  useEffect(() => {
    if (activeView !== "history") return;
    fetchCallTransactions(currentPage);
  }, [activeView, currentPage, fetchCallTransactions]);

  useEffect(() => {
    if (activeView !== "history") return;
    setCurrentPage(1);
    fetchRecentCalls();
  }, [activeView, debouncedSearch, callTypeFilter, statusFilter, fetchRecentCalls]);

  useEffect(()=>{
    fetchOverview()
  },[])
  

    // ================= USER LIST FETCH (COIN EARNING USER BLOCK) =================
  const fetchUsers = useCallback(
    async (pageNumber = userPage, searchQuery = userSearch) => {
      try {
        //new api getusersByCallHistroy
        const res = await axios.get("api/v1/user/getusersByCallHistroy", {
          params: {
            page: pageNumber,
            limit: userLimit,
            search:  searchQuery || "",
          },
        });

        if (res.data.success) {
          setUsers(res.data.data || []);
          setUserTotalPages(res.data.pagination.totalPages || 1);
        } else {
          setUsers([]);
          setUserTotalPages(1);
        }
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    },
    [userPage, userSearch, userLimit,activeView]
  );







const fetchOverview = async () => {
  try {
    const res = await axios.get(
      "/api/v1/app/coin-earnings/call-coins-summary"
    );

    if (res.data.status === "OK") {
      setOverview(res.data.data);
    }
  } catch (err) {
    console.error("Error fetching call coin summary:", err);
  }
};



  useEffect(() => {
    if (activeView !== "user-history") return;
    fetchUsers(userPage, userSearch);
  }, [activeView, fetchUsers, userPage, userSearch]);

  


  const statusOptions = useMemo(() => {
    const base = ["completed", "cancelled", "failed", "ongoing"];
    const seen = new Set();
    base.forEach((s) => seen.add(s));
    users.forEach((u)=> {
      const raw =  u?.lastCallStatus;
      if (!raw) return;
      const norm = String(raw).trim().toLowerCase();
      if (norm) seen.add(norm);
    });
    const dynamic = Array.from(seen).sort((a, b) => a.localeCompare(b));
    return ["all", "no-history", ...dynamic];
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (userCallStatusFilter === "all") return users;
 
    if (userCallStatusFilter === "no-history") {
  return users.filter((u) => !u.lastCallTime);
}
    return users.filter((u) => {
      // const st = userLastCalls[u._id]?.status;
      const st = u.lastCallStatus;
      if (!st) return false;
      return String(st).trim().toLowerCase() === userCallStatusFilter;
    });
  }, [users, userCallStatusFilter]);

  const handleUserSearchChange = (value) => {
    setUserSearch(value);
    if (userSearchTimeout) clearTimeout(userSearchTimeout);

    const newTimeout = setTimeout(() => {
      setUserPage(1);
      fetchUsers(1, value);
    }, 800);
    setUserSearchTimeout(newTimeout);
  };

  const closeTxModal = () => setSelectedTx(null);
  const showValue = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "string" && v.trim().length === 0) return "—";
    return v;
  };

  const openUserDetails = async (userId) => {
    if (!userId) return;
    try {
      const res = await axios.get(`/api/v1/admin/users/${userId}`);
      const u = res?.data?.data || res?.data || {};
      const model = {
        name: u.fullName || u.name || u.displayName || "",
        email: u.email || "",
        gender: u.gender || "",
        phoneNumber: u.phoneNumber || u.phone || "",
        image:
          u.image ||
          u.profileImage ||
          u.avatar ||
          u.profilePhoto ||
          u.photoURL ||
          "",
        followers: u.followers || u.followersCount || 0,
        following: u.following || u.followingCount || 0,
        friends: u.friends || u.friendsCount || 0,
      };
      setViewUser({ model, userId });
    } catch {
      setViewUser({ model: { name: "User", email: "", gender: "" }, userId });
    }
  };

  const openUserFromTx = async (userId) => {
    closeTxModal();
    await openUserDetails(userId);
  };

  useEffect(() => {
    if (!selectedTx) return;
    let cancelled = false;

    const hydratePhones = async () => {
      const callerId = selectedTx?.callerId?._id;
      const receiverId = selectedTx?.receiverId?._id;
      const callerHasPhone = !!(selectedTx?.callerId?.phoneNumber || selectedTx?.callerId?.phone);
      const receiverHasPhone = !!(selectedTx?.receiverId?.phoneNumber || selectedTx?.receiverId?.phone);
      if ((callerHasPhone || !callerId) && (receiverHasPhone || !receiverId)) return;

      try {
        const callerReq = !callerHasPhone && callerId ? axios.get(`/api/v1/admin/users/${callerId}`) : Promise.resolve(null);
        const receiverReq = !receiverHasPhone && receiverId ? axios.get(`/api/v1/admin/users/${receiverId}`) : Promise.resolve(null);
        const [callerRes, receiverRes] = await Promise.all([callerReq, receiverReq]);
        if (cancelled) return;

        const caller = callerRes ? (callerRes?.data?.data || callerRes?.data || {}) : null;
        const receiver = receiverRes ? (receiverRes?.data?.data || receiverRes?.data || {}) : null;

        setSelectedTx((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          if (caller) {
            next.callerId = {
              ...next.callerId,
              phoneNumber: next.callerId?.phoneNumber || caller.phoneNumber || caller.phone || "",
            };
          }
          if (receiver) {
            next.receiverId = {
              ...next.receiverId,
              phoneNumber: next.receiverId?.phoneNumber || receiver.phoneNumber || receiver.phone || "",
            };
          }
          return next;
        });
      } catch {
        // ignore
      }
    };

    hydratePhones();
    return () => {
      cancelled = true;
    };
  }, [selectedTx]);

  const formatDuration = (minutes) => {
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) {
    return "—";
  }

  const totalSeconds = Math.round(minutes * 60);

  if (totalSeconds < 60) {
    return `${totalSeconds} sec`;
  }

  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  return secs === 0
    ? `${mins} min`
    : `${mins} min ${secs} sec`;
};

  return (
    <div className="min-h-screen p-6 bg-white text-[var(--black-text)]">
      {/* Header / Title */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-[var(--primary-solid)] flex items-center gap-3">
            <span className="text-3xl">
              <IoMdCall />
            </span>
            <span>Call History</span>
          </h1>
          <p className="text-sm text-[var(--black-text)]/70 mt-1">
            Monitor and manage all call transactions and earnings
          </p>
        </div>

        <div className="flex items-center gap-3">
          {canExport && (
            <button
              onClick={exportToExcel}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-[var(--black-text)] text-sm flex items-center gap-2 hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white transition"
            >
              ⤓ Export XLS
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button
          onClick={() => setActiveView("history")}
          className={`px-6 py-2 font-semibold rounded-xl border-2 transition ${
            activeView === "history"
              ? "bg-[var(--primary-solid)] text-white border-[var(--primary-solid)] shadow-md"
              : "bg-white text-[var(--primary-solid)] border-gray-200 hover:bg-[var(--primary-solid)]/10"
          }`}
        >
          History
        </button>
        <button
          // onClick={() => setActiveView("user-history")}
  //           onClick={() => {
  //   setActiveView("user-history");
  //   fetchUsers(1, "");
  // }}

  onClick={() => {
  setUserPage(1);
  setUserSearch("");
  setActiveView("user-history");
}}
          className={`px-6 py-2 font-semibold rounded-xl border-2 transition ${
            activeView === "user-history"
              ? "bg-[var(--primary-solid)] text-white border-[var(--primary-solid)] shadow-md"
              : "bg-white text-[var(--primary-solid)] border-gray-200 hover:bg-[var(--primary-solid)]/10"
          }`}
        >
          User History
        </button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-[var(--yellow)]/10 to-[var(--yellow)]/5 border-2 border-[var(--yellow)]/20 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-[color:var(--yellow)/0.8] uppercase tracking-wider">
                Total Transactions
              </p>
              <p className="text-2xl font-bold text-[var(--yellow)]">
                {totalTransactions}
              </p>
            </div>
            <div className="text-3xl text-[var(--yellow)]">
              <IoMdCall />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-2 border-green-500/20 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-green-400 uppercase tracking-wider">
                Voice Call Coins
              </p>
              <p className="text-2xl font-bold text-green-400">
                {overview?.voiceCallCoins || 0}
              </p>
              <p className="text-xs text-green-300 mt-1">total earned</p>
            </div>
            <div className="text-3xl text-green-400">
              <FiUser />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-2 border-blue-500/20 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-blue-400 uppercase tracking-wider">
                Video Call Coins
              </p>
              <p className="text-2xl font-bold text-blue-400">
                {Number(overview?.videoCallCoins || 0).toFixed(2)}
              </p>
              <p className="text-xs text-blue-300 mt-1">total earned</p>
            </div>
            <div className="text-3xl text-blue-400">
              <IoMdCall />
            </div>
          </div>
        </div>
      </div>

      {activeView === "history" && (
        <div className="bg-white p-6 rounded-xl shadow-sm mb-8 border-2 border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-xl font-bold text-[var(--primary-solid)]">Call History</h2>
              <p className="text-[var(--black-text)]/70">All calls across users</p>
            </div>

            <div className="w-full md:w-auto flex flex-col md:flex-row gap-3 items-stretch md:items-center">
              <div className="relative w-full md:w-72">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--primary-solid)] text-xl">
                  <IoSearch />
                </span>
                <input
                  type="text"
                  placeholder="Search calls..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-xl border-2 border-gray-200 bg-white text-[var(--black-text)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                />
              </div>

              {/* <select
                value={callTypeFilter}
                onChange={(e) => setCallTypeFilter(e.target.value)}
                className="w-full md:w-44 px-4 py-2 rounded-xl border-2 border-gray-200 bg-white text-[var(--black-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
              >
                <option value="all">All Types</option>
                <option value="audio">Audio</option>
                <option value="video">Video</option>
              </select> */}

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full md:w-48 px-4 py-2 rounded-xl border-2 border-gray-200 bg-white text-[var(--black-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
              >
                <option value="all">All Status</option>
               <option value="ongoing">Ongoing</option>
<option value="completed">Completed</option>
<option value="failed">Failed</option>
<option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          <div className="mt-6">
            <h3 className="text-lg font-bold text-[var(--primary-solid)] mb-3">Recent Calls</h3>
            {recentCalls.length === 0 ? (
              <div className="text-sm text-gray-500">No recent calls</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recentCalls.map((tx, idx) => {
                  const caller = tx?.callerId?.fullName || tx?.callerId?.name || "—";
                  const receiver = tx?.receiverId?.fullName || tx?.receiverId?.name || "—";
                  const type = tx?.callType || "—";
                  const status = tx?.status || "—";
                  // const duration = typeof tx?.durationMinutes === "number" ? `${tx.durationMinutes} min` : "—";
const duration = formatDuration(tx?.durationMinutes);
                  const dateStr = tx?.callStartTime || tx?.createdAt || null;
                  const when = dateStr ? new Date(dateStr).toLocaleString() : "—";
                  const stNorm = String(status).toLowerCase();
                  const stClass =
                    stNorm === "completed" || stNorm === "success"
                      ? "bg-green-100 text-green-700 border-green-200"
                      : stNorm === "missed" || stNorm === "failed" || stNorm === "rejected"
                      ? "bg-red-100 text-red-700 border-red-200"
                      : stNorm === "cancelled"
                      ? "bg-orange-100 text-orange-700 border-orange-200"
                      : "bg-gray-100 text-gray-600 border-gray-200";
                  return (
                    <div
                      key={idx}
                      className="border-2 border-gray-200 rounded-xl p-4 shadow-sm bg-white cursor-pointer hover:border-[var(--primary-solid)] transition"
                      onClick={() => setSelectedTx(tx)}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-bold text-[var(--black-text)] truncate">
                            {caller} <span className="text-gray-400 font-semibold">→</span> {receiver}
                          </div>
                          <div className="text-xs text-[var(--black-text)]/60 mt-1">{when}</div>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${stClass}`}>
                          {String(status).replace(/_/g, " ").toUpperCase()}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center gap-3 text-sm text-[var(--black-text)]/70">
                        <span className="capitalize">{type}</span>
                        <span>•</span>
                        <span>{duration}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-bold text-[var(--primary-solid)] mb-3">All Calls</h3>
            {transactions.length === 0 ? (
              <div className="text-sm text-gray-500">No calls found</div>
            ) : (
              <div className="overflow-x-auto border-2 border-gray-200 rounded-xl">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white text-xs uppercase font-semibold">
                      <th className="py-3 px-4 text-left">Time</th>
                      <th className="py-3 px-4 text-left">Caller</th>
                      <th className="py-3 px-4 text-left">Deduction</th>
                      <th className="py-3 px-4 text-left">Receiver</th>
                      <th className="py-3 px-4 text-left">Credit</th>
                      <th className="py-3 px-4 text-left">Type</th>
                      <th className="py-3 px-4 text-left">Duration</th>
                                            <th className="py-3 px-4 text-left">Gift Transactions</th>

                      <th className="py-3 px-4 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, idx) => {
                      const dateStr = tx?.callStartTime || tx?.createdAt || null;
                      const when = dateStr ? new Date(dateStr).toLocaleString() : "—";
                      const caller = tx?.callerId?.fullName || tx?.callerId?.name || "—";
                      const receiver = tx?.receiverId?.fullName || tx?.receiverId?.name || "—";
                      const type = tx?.callType || "—";
                      const status = tx?.status || "—";
                      const giftCount = tx?.giftTransactions?.length  || "—";
const duration = formatDuration(tx?.durationMinutes);
                      const stNorm = String(status).toLowerCase();
                      const stClass =
                        stNorm === "completed" || stNorm === "success"
                          ? "bg-green-100 text-green-700 border-green-200"
                          : stNorm === "missed" || stNorm === "failed" || stNorm === "rejected"
                          ? "bg-red-100 text-red-700 border-red-200"
                          : stNorm === "cancelled"
                          ? "bg-orange-100 text-orange-700 border-orange-200"
                          : "bg-gray-100 text-gray-600 border-gray-200";
                      return (
                        <tr
                          key={idx}
                          className="border-t border-gray-200 cursor-pointer hover:bg-gray-50"
                          onClick={() => setSelectedTx(tx)}
                        >
                          <td className="py-3 px-4">{when}</td>
                          <td className="py-3 px-4">{caller}</td>
                          <td className="py-3 px-4 text-red-600 font-semibold">{tx.totalCoins || 0}</td>
                          <td className="py-3 px-4">{receiver}</td>
                          <td className="py-3 px-4 text-green-600 font-semibold">{tx.receiverEarnings || 0}</td>
                          <td className="py-3 px-4 capitalize">{type}</td>
                          <td className="py-3 px-4">{duration}</td>
                                                    <td className="py-3 px-4">{giftCount}</td>

                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${stClass}`}>
                              {String(status).replace(/_/g, " ").toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-center mt-6">
              <div className="flex items-center gap-2 bg-white border-2 border-gray-200 px-4 py-3 rounded-xl shadow-sm">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-2 rounded-lg text-sm font-semibold transition bg-[var(--yellow)] text-[var(--black)] disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
                >
                  <FaChevronCircleLeft />
                </button>
                <span className="text-sm font-semibold text-[var(--black-text)]">
                  Page {currentPage} / {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-2 rounded-lg text-sm font-semibold transition bg-[var(--yellow)] text-[var(--black)] disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
                >
                  <FaCircleChevronRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeView === "user-history" && (
      <div className="bg-white p-6 rounded-xl shadow-sm mb-8 border-2 border-gray-200">
        {/* TOP ROW */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold text-[var(--primary-solid)]">
              Users Call History
            </h2>
            <p className="text-[var(--black-text)]/70">
              Overview of Users  Call History
            </p>
          </div>

          <div className="w-full md:w-auto flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative w-full md:w-72">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--primary-solid)] text-xl">
                <IoSearch />
              </span>

              <input
                type="text"
                placeholder="Search users ..."
                value={userSearch}
                onChange={(e) => handleUserSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border-2 border-gray-200 bg-white text-[var(--black-text)] 
                              placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)] 
                              focus:ring-2 focus:ring-[var(--yellow)]"
              />
            </div>

            <select
              value={userCallStatusFilter}
              onChange={(e) => setUserCallStatusFilter(e.target.value)}
              className="w-full md:w-56 px-4 py-2 rounded-xl border-2 border-gray-200 bg-white text-[var(--black-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s === "all"
                    ? "All Status"
                    : s === "no-history"
                    ? "No History"
                    : s.replace(/_/g, " ").toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* USER LIST */}
        <div className="mt-4">
          {filteredUsers.length === 0 ? (
            <p className="text-center text-gray-400 py-6">No users found</p>
          ) : (
            filteredUsers.map((user) => {
              // Format duration helper
              const formatDuration = (minutes) => {
                if (!minutes && minutes !== 0) return "—";
                if (minutes < 1) return "< 1 min";
                if (minutes < 60) return `${Math.round(minutes)} min`;
                const hours = Math.floor(minutes / 60);
                const mins = Math.round(minutes % 60);
                return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
              };

              // Format date helper
              const formatDate = (dateString) => {
                if (!dateString) return "—";
                try {
                  const date = new Date(dateString);
                  const now = new Date();
                  const diffMs = now - date;
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMins / 60);
                  const diffDays = Math.floor(diffHours / 24);

                  if (diffMins < 1) return "Just now";
                  if (diffMins < 60) return `${diffMins} min ago`;
                  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
                  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
                  return date.toLocaleDateString();
                } catch {
                  return "—";
                }
              };



              const lastSpokenPerson = user.lastSpokenUser || "—";
const lastCallDuration = user.lastCallDurationMinutes;
const lastCallType = user.lastCallType || "—";
const lastCallDate = user.lastCallTime || null;
const lastCallStatus = user.lastCallStatus || "—";
              const statusNorm = String(lastCallStatus).trim().toLowerCase();
              const statusClass =
                statusNorm === "completed" || statusNorm === "success"
                  ? "bg-green-100 text-green-700 border-green-200"
                  : statusNorm === "missed" || statusNorm === "failed" || statusNorm === "rejected"
                  ? "bg-red-100 text-red-700 border-red-200"
                  : statusNorm === "cancelled"
                  ? "bg-orange-100 text-orange-700 border-orange-200"
                  : "bg-gray-100 text-gray-600 border-gray-200";

              return (
                <div
                  onClick={() =>
                    navigate(`/call-transactions/${user._id}`)
                  }
                  key={user._id}
                  className="border-2 border-gray-200 rounded-xl p-4 mb-4 shadow-sm bg-white
                                    hover:border-[var(--primary-solid)] hover:border-2 transition cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    {/* <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0">
                      <FiUser className="text-white" />
                    </div> */}

                    <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center">
    {user.profileImage && user.profileImage !== "default-profile.jpg" ? (
        <img

        loading="lazy"
            src={user.profileImage}
            alt={user.fullName}
            className="w-full h-full object-cover"
            onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default-avatar.png"; // optional fallback
            }}
        />
    ) : (
        <FiUser className="text-gray-500 text-xl" />
    )}
</div>


                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[var(--black-text)] text-lg">
                        {user.fullName || "N/A"}
                      </p>
                      <p className="text-[var(--black-text)]/70 text-sm">
                        {user.phoneNumber}
                      </p>
                    </div>

                    {/* Last Call Details */}
                    {user.lastCallTime  ? (
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm text-[var(--black-text)]/80">
                          <span className="font-medium">Last spoken: </span>
                          <span className="text-[var(--primary-solid)]">{lastSpokenPerson}</span>
                        </div>
                        <div className="text-sm text-[var(--black-text)]/70 mt-1">
                          <span className="font-medium">Duration: </span>
                          <span>{formatDuration(lastCallDuration)}</span>
                        </div>
                        <div className="text-sm text-[var(--black-text)]/70 mt-1">
                          <span className="font-medium">Status: </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${statusClass}`}>
                            {String(lastCallStatus).replace(/_/g, " ").toUpperCase()}
                          </span>
                        </div>
                        <div className="text-xs text-[var(--black-text)]/60 mt-1 flex items-center justify-end gap-2">
                          <span className="capitalize">{lastCallType}</span>
                          {lastCallDate && (
                            <>
                              <span>•</span>
                              <span>{formatDate(lastCallDate)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm text-[var(--black-text)]/60">
                          No call history
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* USER PAGINATION */}
        <div className="flex items-center justify-center mt-8">
          <div className="flex items-center gap-2 bg-white border-2 border-gray-200 px-4 py-3 rounded-xl shadow-sm">
            <button
              disabled={userPage === 1}
              onClick={() => setUserPage(userPage - 1)}
              className="px-3 py-2 rounded-lg text-sm font-semibold transition bg-[var(--yellow)] text-[var(--black)]
                        disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
            >
              <FaChevronCircleLeft />
            </button>

            <div className="flex items-center gap-2">
              {(() => {
                let pages = [];
                const createPageBtn = (p) => (
                  <button
                    key={p}
                    onClick={() => setUserPage(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-semibold border transition 
                                        ${p === userPage
                        ? "bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white border-gray-200"
                        : "text-[var(--black-text)] border-2 border-gray-200 hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white"
                      }`}
                  >
                    {p}
                  </button>
                );

                const createDot = (key) => (
                  <span
                    key={key}
                    className="text-[var(--primary-solid)] font-bold px-2"
                  >
                    …
                  </span>
                );

                if (userTotalPages <= 7) {
                  for (let i = 1; i <= userTotalPages; i++)
                    pages.push(createPageBtn(i));
                } else {
                  pages.push(createPageBtn(1));
                  pages.push(createPageBtn(2));

                  if (userPage > 4) pages.push(createDot("start"));

                  let start = Math.max(3, userPage - 1);
                  let end = Math.min(userTotalPages - 2, userPage + 1);

                  for (let i = start; i <= end; i++)
                    pages.push(createPageBtn(i));

                  if (userPage < userTotalPages - 3)
                    pages.push(createDot("end"));

                  pages.push(createPageBtn(userTotalPages - 1));
                  pages.push(createPageBtn(userTotalPages));
                }

                return pages;
              })()}
            </div>

            <button
              disabled={userPage === userTotalPages}
              onClick={() => setUserPage(userPage + 1)}
              className="px-3 py-2 rounded-lg text-sm font-semibold transition bg-[var(--yellow)] text-[var(--black)]
                        disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
            >
              <FaCircleChevronRight />
            </button>
          </div>
          <div className="w-full md:w-40">
  <select
    value={userLimit}
    onChange={(e) => {
      setUserLimit(Number(e.target.value));
      setUserPage(1);
    }}
    className="w-full px-4 py-2 rounded-xl border-2 border-gray-200 bg-white text-[var(--black-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
  >
    <option value={10}>10 Rows</option>
    <option value={25}>25 Rows</option>
    <option value={50}>50 Rows</option>
    <option value={100}>100 Rows</option>
  </select>
</div>
        </div>
      </div>
      )}

      {selectedTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeTxModal}>
          <div
            className="bg-white w-full max-w-3xl rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="text-lg font-bold text-[var(--primary-solid)]">Call Transaction Details</div>
              <button
                onClick={closeTxModal}
                className="text-2xl leading-none text-gray-500 hover:text-gray-800"
              >
                &times;
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Caller</div>
                  <div className="text-sm">
                    <button
                      className="font-semibold text-[var(--primary-solid)] hover:underline text-left"
                      onClick={() => openUserFromTx(selectedTx?.callerId?._id)}
                      type="button"
                    >
                      {showValue(selectedTx?.callerId?.fullName || selectedTx?.callerId?.name)}
                    </button>
                    <div className="text-gray-500 mt-1">ID: {showValue(selectedTx?.callerId?._id)}</div>
                    <div className="text-gray-500 mt-1">Phone: {showValue(selectedTx?.callerId?.phoneNumber || selectedTx?.callerId?.phone)}</div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Receiver</div>
                  <div className="text-sm">
                    <button
                      className="font-semibold text-[var(--primary-solid)] hover:underline text-left"
                      onClick={() => openUserFromTx(selectedTx?.receiverId?._id)}
                      type="button"
                    >
                      {showValue(selectedTx?.receiverId?.fullName || selectedTx?.receiverId?.name)}
                    </button>
                    <div className="text-gray-500 mt-1">ID: {showValue(selectedTx?.receiverId?._id)}</div>
                    <div className="text-gray-500 mt-1">Phone: {showValue(selectedTx?.receiverId?.phoneNumber || selectedTx?.receiverId?.phone)}</div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Call</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Type</div>
                    <div className="font-semibold capitalize">{showValue(selectedTx?.callType)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Status</div>
                    <div className="font-semibold">{showValue(selectedTx?.status || selectedTx?.callStatus || selectedTx?.state)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Duration</div>
                    <div className="font-semibold">
                      {formatDuration(selectedTx?.durationMinutes)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Start Time</div>
                    <div className="font-semibold">{showValue(selectedTx?.callStartTime ? new Date(selectedTx.callStartTime).toLocaleString() : null)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">End Time</div>
                    <div className="font-semibold">{showValue(selectedTx?.callEndTime ? new Date(selectedTx.callEndTime).toLocaleString() : null)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Created</div>
                    <div className="font-semibold">{showValue(selectedTx?.createdAt ? new Date(selectedTx.createdAt).toLocaleString() : null)}</div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Balance Changes</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <div className="text-gray-500 font-medium">Caller Balance</div>
                    <div className="mt-2 flex items-center gap-2 text-gray-700">
                      <span>Before:</span>
                      <span className="font-semibold">{Number(showValue(selectedTx?.callerBalanceBefore)).toFixed(2)}</span>
                      <span>→</span>
                      {/* <span className="font-semibold">{showValue(selectedTx?.callerBalanceAfter)}</span> */}
                                            <span className="font-semibold">{Number(showValue(selectedTx?.callerBalanceAfter)).toFixed(2)}</span>

                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-medium">Receiver Balance</div>
                    <div className="mt-2 flex items-center gap-2 text-gray-700">
                      <span>Before:</span>
                      <span className="font-semibold">{Number(showValue(selectedTx?.receiverBalanceBefore)).toFixed(2)}</span>
                      <span>→</span>
                      <span className="font-semibold">{Number(showValue(selectedTx?.receiverBalanceAfter)).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Coins</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Total Coins</div>
                    <div className="font-semibold">{showValue(selectedTx?.totalCoins)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Admin Commission</div>
                    <div className="font-semibold">{showValue(selectedTx?.adminCommissionAmount)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Receiver Earnings</div>
                    <div className="font-semibold">{showValue(selectedTx?.receiverEarnings)}</div>
                  </div>
                </div>
              </div>



<div className="border border-gray-200 rounded-xl p-4">
  <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">
    🎁 Gift Transactions
  </div>

  {!selectedTx?.giftTransactions ||
  selectedTx.giftTransactions.length === 0 ? (
    <div className="text-center py-6 text-gray-500">
      No gifts were exchanged during this call.
    </div>
  ) : (
    <div className="space-y-4">
      {selectedTx.giftTransactions.map((gift, index) => {
        const transaction = gift?.giftTransactionId;
        const giftInfo = transaction?.giftId || gift?.giftId;

        return (
          <div
            key={index}
            className="border rounded-xl p-4 bg-gray-50 border-gray-200"
          >
            <div className="flex items-start gap-4">

              <div className="w-20 h-20 rounded-xl overflow-hidden bg-white border flex items-center justify-center">
                {giftInfo?.image?.url ? (
                  <img
                    src={giftInfo?.image?.url}
                    alt={giftInfo?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl">🎁</span>
                )}
              </div>

              <div className="flex-1">

                <h3 className="font-bold text-lg">
                  {showValue(giftInfo?.name || gift?.giftName)}
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-sm">

                  <div>
                    <div className="text-gray-500">
                      Sender
                    </div>

                    <div className="font-semibold">
                      {showValue(transaction?.senderId?.fullName)}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      Receiver
                    </div>

                    <div className="font-semibold">
                      {showValue(transaction?.receiverId?.fullName)}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      Category
                    </div>

                    <div className="font-semibold">
                      {showValue(giftInfo?.category)}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      Gift Coins
                    </div>

                    <div className="font-semibold text-red-600">
                      {showValue(transaction?.coinAmount)}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      Admin Commission %
                    </div>

                    <div className="font-semibold">
                      {showValue(transaction?.adminCommissionPercentage)}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      Admin Commission
                    </div>

                    <div className="font-semibold">
                      {showValue(transaction?.adminCommission)}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      Receiver Earnings
                    </div>

                    <div className="font-semibold text-green-600">
                      {showValue(transaction?.receiverEarnings)}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      Gift Time
                    </div>

                    <div className="font-semibold">
                      {showValue(
                        transaction?.createdAt
                          ? new Date(
                              transaction.createdAt
                            ).toLocaleString()
                          : null
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      Gift Transaction ID
                    </div>

                    <div className="font-semibold break-all">
                      {showValue(transaction?._id)}
                    </div>
                  </div>

                </div>

                <div className="mt-4">

                  <div className="text-gray-500 text-sm">
                    Message
                  </div>

                  <div className="font-medium">
                    {showValue(transaction?.message)}
                  </div>

                </div>

              </div>

            </div>
          </div>
        );
      })}
    </div>
  )}
</div>




              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">IDs</div>
                <div className="text-sm text-gray-700 space-y-2">
                  <div>Transaction ID: {showValue(selectedTx?._id || selectedTx?.id)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {viewUser && (
        <UserDetailsModal
          model={viewUser.model}
          userId={viewUser.userId}
          onClose={() => setViewUser(null)}
        />
      )}
    </div>
  );
}
